import { clsx } from '@alexis/helpers/clsx';
import { getCookie } from '@alexis/helpers/cookie';
import { toISOStringWithTimezone } from '@alexis/helpers/date';
import { genzoTrackActionEvent, genzoTrackPageView } from '@alexis/helpers/genzo';
import { gtmTrack } from '@alexis/helpers/gtm';
import { generateCUID } from '@alexis/helpers/identity';
import { translateNumber } from '@alexis/helpers/translation';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import sha256 from 'crypto-js/sha256';
import { uniq } from 'lodash-es';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Dialog from '@components/Dialog';
import LegDetailsCarouselContainer from '@components/flights/TripDetail/LegDetailsCarouselContainer';
import TripLegSummary from '@components/flights/TripDetail/TripLegSummary';
import TripFares from '@components/flights/TripFares';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';
import { GTM_ID } from '@constants/general';

import { useFlightContext } from '@context/FlightContext';
import { GlobalContext } from '@context/GlobalContext';

import {
  filterFlightFareResultFares,
  getNameForDataTracking,
  getOperatingAirlinesInformation,
  isMulticity,
  isRoundTrip,
} from '@helpers/flight';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { airlinesRoute } from '@helpers/routeTranslation';
import { webEngageTrackTripView } from '@helpers/webEngage';

import CloseIcon from '@icons/close.svg';
import FlightIcon from '@icons/flight.svg';
import HourglassIcon from '@icons/hourglass_half.svg';
import InfoIcon from '@icons/info.svg';
import MoonIcon from '@icons/moon_outlined.svg';

import { useGetFlightAmenities } from '@hooks/api/flights/useGetFlightAmenities';
import { useGetFlightMetasearchDetails } from '@hooks/api/flights/useGetFlightMetasearchDetails';
import { useGetFlightTripDetails } from '@hooks/api/flights/useGetFlightTripDetails';
import { useBodyScrollable } from '@hooks/useBodyScrollable';
import { useTripDetailImpressionLogging } from '@hooks/useTripDetailImpressionLogging';

import {
  getCurrencyState,
  getCurrentSiteState,
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { CabinClass } from '@wegoTypes/flights/cabinClass';
import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';
import type { FlightFareResultTripLeg } from '@wegoTypes/flights/metasearch/flightFareResultTripLeg';
import type { FlightSearchResultLeg } from '@wegoTypes/flights/metasearch/flightSearchResultLeg';
import type { TripViewModel } from '@wegoTypes/flights/tripViewModel';

import styles from './TripDetail.module.scss';

interface TripDetailProps {
  clientId: string | undefined;
  clientSessionId: string | undefined;
  gtmAuth: string;
  gtmPreview: string;
  handleOnOverlayClick?(): void;
  localeParam: string | undefined;
  nearestCityByIP: Place | undefined;
  paymentMethodsFromApi: Array<HomepageFlightsPaymentMethod>;
  tripViewModel?: TripViewModel | undefined;
}

const TripDetail: React.FC<TripDetailProps> = ({
  clientId,
  clientSessionId,
  gtmAuth,
  gtmPreview,
  handleOnOverlayClick,
  localeParam,
  nearestCityByIP,
  paymentMethodsFromApi,
  tripViewModel,
}) => {
  const { webEngageAnonymousId } = useContext(GlobalContext);
  const currency = useSelector(getCurrencyState);
  const currentSite = useSelector(getCurrentSiteState);
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);
  const user = useSelector(getUserState);

  const { isIncludeAirlineBackfill } = useFlightContext();

  const { isPerLegSearch } = useFlightPLS();

  const pageViewId = useRef<string>(generateCUID());

  const segmentsDetailsRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [itemWidth, setItemWidth] = useState<number>(0);

  const [isLegExpanded, setIsLegExpanded] = useState<boolean>(false);

  const [, setIsBodyScrollable] = useBodyScrollable();

  const [activeLeg, setActiveLeg] = useState<number>(0);

  const [specialFlightHandoffUrl, setSpecialFlightHandoffUrl] = useState<string>();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  //#region search params
  const [searchParams] = useSearchParams();

  const searchId = searchParams.get('search_id');
  const selectedTripIdSearchParam = searchParams.get('selected_trip_id');
  const paymentMethodsSearchParam = searchParams.get('payment_methods');
  const providersSearchParam = searchParams.get('providers') ?? '';
  const providerCodesSearchParam = searchParams.get('provider_codes') ?? '';

  const selectedPaymentMethodIdsSearchParam = searchParams.get(
    FlightSearchResultSearchParams.SelectedPaymentMethod,
  );

  const paymentMethods = useMemo<Array<number>>(() => {
    if (paymentMethodsSearchParam) {
      return paymentMethodsSearchParam.split(',').map((paymentMethod) => parseInt(paymentMethod));
    }
    return [];
  }, [paymentMethodsSearchParam]);

  const { activeLeg: legSelectedInFlightSearchResults } = useFlightSearchFilters();

  // Fetch Metasearch details
  const { data: flightMetasearchInfo } = useGetFlightMetasearchDetails(
    { searchId: searchId ?? '' },
    { enabled: !!searchId },
  );

  // Trigger flight fare
  const { data: flightFareResult, isLoading: isLoadingFares } = useGetFlightTripDetails(
    {
      tripId: selectedTripIdSearchParam ?? '',
      paymentMethodIds: paymentMethods,
      clientId,
      includeAirlineBackfill: isIncludeAirlineBackfill,
    },
    {
      enabled: !!selectedTripIdSearchParam,
      select: (flightFareResult) => {
        const fares = flightFareResult.trip.fares;
        const airlineBackfillFareIndex = fares.findIndex((fare) =>
          fare.legsSourceTypes?.includes('AIRLINE_BACKFILL'),
        );

        if (airlineBackfillFareIndex > -1) {
          const [airLineBackfillFare] = fares.splice(airlineBackfillFareIndex, 1);

          // Insert airline backfill fare to the 10th position or last
          const newPosition = Math.min(fares.length, 9);
          fares.splice(newPosition, 0, airLineBackfillFare);
        }

        return {
          ...flightFareResult,
          trip: {
            ...flightFareResult.trip,
            fares,
          },
        };
      },
      onErrorCallback: () => {
        const searchParams = new URLSearchParams(search);
        searchParams.delete('selected_trip_id');
        searchParams.delete('search_id');

        navigate({ pathname, search: searchParams.toString() }, { replace: true, state });
      },
    },
  );

  const specialFlightMessages = flightFareResult?.trip?.messages || [];

  // Fetch flight amenities
  const { data: flightAmenities } = useGetFlightAmenities(
    {
      tripId: flightFareResult?.trip.id || '',
    },
    {
      enabled: Boolean(flightFareResult?.trip.id),
    },
  );

  const selectedPaymentMethodIdsFromParam = useMemo<Array<number>>(() => {
    if (selectedPaymentMethodIdsSearchParam?.split(':')[legSelectedInFlightSearchResults]) {
      return (
        selectedPaymentMethodIdsSearchParam.split(':')[legSelectedInFlightSearchResults] || ''
      )
        .split(',')
        .map((item) => parseInt(item));
    }

    return [];
  }, [legSelectedInFlightSearchResults, selectedPaymentMethodIdsSearchParam]);
  //#endregion

  const impressionLogging = useTripDetailImpressionLogging(
    clientId,
    clientSessionId,
    [...(flightFareResult?.trip.fares || []), ...(flightFareResult?.trip.recommendedFares || [])],
    searchId || undefined,
    pageViewId.current,
    flightFareResult?.trip.id,
    user,
  );

  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();

  const legs = useMemo<FlightSearchResultSearchLeg[]>(() => {
    if (flightMetasearchInfo) {
      return flightMetasearchInfo.legs;
    }
    return [];
  }, [flightMetasearchInfo]);

  const adultCount = useMemo<number>(() => {
    if (flightMetasearchInfo) {
      return flightMetasearchInfo.adultsCount;
    }
    return 0;
  }, [flightMetasearchInfo]);

  const childCount = useMemo<number>(() => {
    if (flightMetasearchInfo) {
      return flightMetasearchInfo.childrenCount;
    }
    return 0;
  }, [flightMetasearchInfo]);

  const infantCount = useMemo<number>(() => {
    if (flightMetasearchInfo) {
      return flightMetasearchInfo.infantsCount;
    }
    return 0;
  }, [flightMetasearchInfo]);

  const cabinClass = useMemo<CabinClass>(() => {
    if (flightMetasearchInfo) {
      return flightMetasearchInfo.cabin;
    }
    return 'economy';
  }, [flightMetasearchInfo]);

  const passengerCount = useMemo(
    () => adultCount + childCount + infantCount,
    [adultCount, childCount, infantCount],
  );

  const faresForFlightViewRatesWebengageTracking = useMemo(() => {
    if (flightFareResult) {
      const { filteredNormalFares, filteredRecommendedFares } = filterFlightFareResultFares(
        flightFareResult.trip,
        [],
        providerCodesSearchParam,
        providersSearchParam,
      );

      const fares = [...filteredNormalFares, ...filteredRecommendedFares].sort(
        (a, b) => a.price.amountUsd - b.price.amountUsd,
      );

      return fares;
    }
  }, [flightFareResult, providerCodesSearchParam, providersSearchParam]);

  const handleLeftChevronClick = useCallback<() => void>((): void => {
    setActiveLeg((prev) => prev - 1);
  }, []);

  const handleRightChevronClick = useCallback<() => void>((): void => {
    setActiveLeg((prev) => prev + 1);
  }, []);

  // Expands or callapses leg segment details
  const expandCollapseLegDetails = useCallback<
    (isLegDetailExpanded: boolean, legIndex: number) => void
  >(
    (isLegDetailExpanded: boolean, legIndex: number) => {
      setIsLegExpanded(isLegDetailExpanded);

      if (!!clientId && !!clientSessionId) {
        const eventData = {
          id: pageViewId.current,
          category: 'flights_detail_page',
          object: 'leg_details',
          action: isLegDetailExpanded ? 'show_details' : 'hide_details',
          value: `${legIndex + 1}`,
        };

        genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
      }

      const gtmData = {
        event: 'eventTracked',
        eventAction: 'detail',
        eventCategory: 'flights',
        eventLabel: 'expand',
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
    },
    [clientId, clientSessionId, gtmAuth, gtmPreview, pageViewId.current, user?.userHash],
  );

  const renderLegLabel = useCallback<(depart: string, arrival: string) => string>(
    (depart, arrival) => {
      if (isRtl) {
        return `${arrival} ← ${depart}`;
      }

      return `${depart} → ${arrival}`;
    },
    [isRtl],
  );

  const displayViaText = useCallback(
    (stopovers) => {
      return stopovers && stopovers.length
        ? ' ' + translations.via + ' ' + stopovers.join(', ')
        : '';
    },
    [translations],
  );

  // ImpressionLogging on Page Load
  useEffect(() => {
    if (flightFareResult) {
      impressionLogging.handlePageLoad();
    }
  }, [flightFareResult]);

  useEffect(() => {
    if (!!flightFareResult && legs.length) {
      webEngageTrackTripView(
        currentSite.countryCode,
        locale,
        adultCount,
        childCount,
        infantCount,
        cabinClass,
        legs,
        flightFareResult.trip,
        selectedPaymentMethodIdsFromParam,
        !!providerCodesSearchParam
          ? providerCodesSearchParam.split(':')[
              isPerLegSearch ? legSelectedInFlightSearchResults : 0
            ]
          : '',
        !!providersSearchParam
          ? providersSearchParam.split(':')[isPerLegSearch ? legSelectedInFlightSearchResults : 0]
          : '',
      );

      // Track flight_view_rates event if there are more than 4 fares
      if (
        faresForFlightViewRatesWebengageTracking &&
        faresForFlightViewRatesWebengageTracking.length >= 4
      ) {
        webEngageTrackTripView(
          currentSite.countryCode,
          locale,
          adultCount,
          childCount,
          infantCount,
          cabinClass,
          legs,
          flightFareResult.trip,
          selectedPaymentMethodIdsFromParam,
          providerCodesSearchParam,
          providersSearchParam,
          true,
          faresForFlightViewRatesWebengageTracking,
        );
      }
    }
  }, [
    adultCount,
    cabinClass,
    childCount,
    currentSite.countryCode,
    flightFareResult,
    infantCount,
    legs,
    locale,
    faresForFlightViewRatesWebengageTracking,
  ]);

  // Genzo Tracking
  useEffect(() => {
    if (!!legs.length && !!clientId && !!clientSessionId && !!webEngageAnonymousId) {
      const clientData: any = {
        id: clientId,
        session_id: clientSessionId,
      };

      if (user) {
        clientData.user_hash = user.userHash;
      }

      const name = `${getNameForDataTracking('Flights Details', legs)}: ${
        !!specialFlightMessages && specialFlightMessages.length > 0
          ? specialFlightMessages[0].header
          : ''
      }`;

      const pageData: any = {
        name,
        product: 'flights',
        base_type: 'flights_detail_page',
        sub_type: `${isMulticity(legs) ? 'multicity_' : ''}flights_detail_page`,
        locale,
        site_code: currentSite.countryCode,
        url: window.location.href,
        referrer_url: document.referrer,
      };

      const data: any = {
        id: pageViewId.current,
        client: clientData,
        event_id: selectedTripIdSearchParam,
        page: pageData,
        created_at: toISOStringWithTimezone(new Date()),
        external_services: {
          crm_id: user ? user.userHash : webEngageAnonymousId,
        },
      };

      genzoTrackPageView(API_BASE_URL, data);
    }
  }, [
    clientId,
    clientSessionId,
    currentSite.countryCode,
    flightMetasearchInfo?.legs.length,
    legs,
    locale,
    selectedTripIdSearchParam,
    specialFlightMessages,
    user,
    webEngageAnonymousId,
  ]);

  // Gtm Tracking
  useEffect(() => {
    if (legs.length && !!selectedTripIdSearchParam && !!clientSessionId && !!nearestCityByIP) {
      const pageName = getNameForDataTracking('Flights Details', legs);

      const pageData: FlightTripDetailGtmPageData = {
        client_type: 'WEB_APP',
        url: window.location.href,
        domain: window.location.hostname,
        locale,
        site_code: currentSite.countryCode,
        user_country_code: nearestCityByIP.countryCode,
        session_id: clientSessionId,
        user_id: getCookie('wego_user_id') ?? null,
        ts_code: getCookie('wego_ts_code') ?? null,
        event: 'pageChanged',
        page_name: pageName,
        page_type: 'flights_detail_page',
        page_sub_type: `${isMulticity(legs) ? 'multicity_' : ''}flights_detail_page`,
        product: 'flights',
        currency_code: currency.code,
        trip_code: selectedTripIdSearchParam ?? '',
        ...(!!user?.email && { e_id: sha256(user?.email).toString() }),
      };

      gtmTrack(GTM_ID, gtmAuth, gtmPreview, pageData);
    }
  }, [
    clientSessionId,
    currency.code,
    currentSite.countryCode,
    gtmAuth,
    gtmPreview,
    legs,
    locale,
    nearestCityByIP,
    selectedTripIdSearchParam,
    user,
  ]);

  // Disable body scroll if dialog is open
  useEffect(() => {
    setIsBodyScrollable(false);

    return () => {
      setIsBodyScrollable(true);
    };
  }, []);

  useEffect(() => {
    segmentsDetailsRefs.current = segmentsDetailsRefs.current.slice(0, legs.length);
  }, [legs]);

  // Make the segment details section of each leg to be the max of the heights of each leg to align the details in the same row.
  useEffect(() => {
    if (segmentsDetailsRefs.current.every((segmentsDetailsRef) => !!segmentsDetailsRef)) {
      if (!isLegExpanded) {
        segmentsDetailsRefs.current.forEach((segmentsDetailContainer) => {
          segmentsDetailContainer!.style.height = `auto`;
        });
      } else {
        const maxHeightLegDetailContainer =
          segmentsDetailsRefs.current.reduce<HTMLDivElement | null>(
            (maxHeightSegmentsDetailRef, legDetailRef) => {
              return !maxHeightSegmentsDetailRef ||
                maxHeightSegmentsDetailRef.offsetHeight < legDetailRef.offsetHeight
                ? legDetailRef
                : maxHeightSegmentsDetailRef;
            },
            null,
          );

        if (maxHeightLegDetailContainer?.offsetHeight) {
          segmentsDetailsRefs.current.forEach((segmentsDetailContainer) => {
            segmentsDetailContainer!.style.height = `${
              maxHeightLegDetailContainer!.offsetHeight
            }px`;
          });
        }
      }
    }
  }, [segmentsDetailsRefs.current?.length, isLegExpanded]);

  const displaySpecialFlightBanner = (leg: FlightFareResultTripLeg | FlightSearchResultLeg) => {
    // TODO remove split('-') once the BE changes the response
    const segmentDesignatorCodes = leg.segments.map(
      (segment) => segment.designatorCode.split('-')[0],
    );
    const specialFlightMessage = specialFlightMessages.find((specialFlightMessage) =>
      segmentDesignatorCodes.includes(specialFlightMessage.segmentId),
    );

    return specialFlightMessage ? (
      <div className={styles.specialFlightMessages}>
        <div className={styles.specialFlightMessage}>
          <InfoIcon className={styles.icon} />
          <span>{specialFlightMessage.header}</span>
        </div>

        <div className={clsx(styles.tooltip, styles.bottom)}>
          <div>{specialFlightMessage.title}</div>
          <div dangerouslySetInnerHTML={{ __html: specialFlightMessage.messages }} />
        </div>
      </div>
    ) : null;
  };

  const handleOnSpecialFlightPopupClose = (
    eventName: 'cancel' | 'confirm' | 'close',
    e?: React.MouseEvent<HTMLDivElement | HTMLAnchorElement, MouseEvent>,
  ) => {
    setIsDialogOpen(false);
    e?.stopPropagation();

    if (!!clientId && !!clientSessionId) {
      const eventData: any = {
        id: pageViewId.current,
        category: 'flights_trip_details',
        object: 'special_flight_popup',
        action: 'click',
        value: eventName,
      };
      genzoTrackActionEvent(API_BASE_URL, clientId, clientSessionId, user?.userHash, eventData);
    }
  };

  const setRef = (index: number, ref: HTMLDivElement | null) => {
    segmentsDetailsRefs.current[index] = ref;
  };

  return (
    <div className={clsx(styles.container, isRtl && styles.rtl)} onClick={handleOnOverlayClick}>
      <div className={styles.popupContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>{translations.flight_details}</div>

          <CloseIcon
            data-pw='tripDetails_closeBtn'
            className={styles.icon}
            onClick={handleOnOverlayClick}
          />
        </div>

        <div className={styles.tripDetails}>
          {/* Itinerary Container */}
          <div className={styles.itineraryContainer}>
            <div className={styles.itineraryHeader}>
              <div className={styles.iteneraryTitle}>{translations.itineary}</div>

              <a
                className={styles.airlinesLink}
                href={`${airlinesRoute(currentSite, localeParam)}/fees`}
                target='_blank'
                rel='noreferrer'
              >
                <InfoIcon className={styles.icon} />

                <span>{translations.airline_fees}</span>
              </a>
            </div>

            {legs.length > 2 ? (
              <div className={styles.multiCityContainer}>
                <div className={styles.multiCity}>
                  {legs.map((leg, index) => (
                    <div
                      key={index}
                      className={clsx(styles.multiCityItem, activeLeg === index && styles.active)}
                      onClick={() => setActiveLeg(index)}
                    >
                      <div className={styles.itemIndex}>{index + 1}</div>

                      <div className={styles.multiCityItemContent}>
                        {renderLegLabel(
                          leg.departureAirport?.code || leg.departureCity.code || '',
                          leg.arrivalAirport?.code || leg.arrivalCity.code || '',
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Carousel for leg details */}
            <LegDetailsCarouselContainer
              activeLeg={activeLeg}
              itemWidth={itemWidth}
              numberOfLegs={tripViewModel?.legs.length || flightFareResult?.trip.legs.length || 0}
              handleLeftChevronClick={handleLeftChevronClick}
              handleRightChevronClick={handleRightChevronClick}
              setItemWidth={setItemWidth}
            >
              {/* TripViewModel is only available when there isn't any results from trip id endpoint */}
              {!flightFareResult && !!tripViewModel
                ? tripViewModel.legs.map((legViewModel, index) => {
                    const cardTitle =
                      legs.length === 1
                        ? translations.depart
                        : legs.length === 2 && isRoundTrip(legs)
                        ? index === 0
                          ? translations.depart
                          : translations.return
                        : `${translations.flight} ${index + 1}`;
                    const legIndex = index;

                    return (
                      <React.Fragment key={legIndex}>
                        <div
                          data-pw='tripDetails_itineraryCard'
                          className={clsx(
                            styles.itineraryCard,
                            legs.length > 2 && activeLeg === legIndex && styles.active,
                          )}
                          style={{ width: `${itemWidth}px` }}
                        >
                          {specialFlightMessages.length > 0
                            ? displaySpecialFlightBanner(legViewModel.leg)
                            : null}
                          <div className={styles.itineraryCardTitle}>{cardTitle}</div>

                          <div className={styles.flightDetails}>
                            <div
                              data-pw='itineraryCard_legDepartureInfo'
                              className={styles.departureColumn}
                            >
                              <div className={styles.date}>{legViewModel.leg.departureDate}</div>

                              <div className={styles.airportCode}>
                                <span>{legViewModel.leg.departureAirportCode}</span>

                                <span>{legViewModel.leg.departureTime}</span>
                              </div>

                              <div className={styles.airportName}>
                                {legViewModel.departureAirport.name}
                              </div>
                            </div>

                            <FlightIcon className={styles.icon} />

                            <div
                              data-pw='itineraryCard_legArrivalInfo'
                              className={styles.arrivalColumn}
                            >
                              <div
                                className={clsx(
                                  styles.date,
                                  legViewModel.leg.durationDays > 0 && styles.oneOrMoreNights,
                                )}
                              >
                                {legViewModel.leg.arrivalDate}
                              </div>
                              <div className={styles.airportCode}>
                                <span>{legViewModel.leg.arrivalAirportCode}</span>

                                <span className={styles.arrivalTimeContainer}>
                                  {legViewModel.leg.arrivalTime}

                                  {legViewModel.leg.durationDays > 0 ? (
                                    <div className={styles.nightCount}>
                                      (+
                                      {translateNumber(
                                        legViewModel.leg.durationDays,
                                        locale === 'fa',
                                      )}
                                      )
                                    </div>
                                  ) : null}
                                </span>
                              </div>

                              <div className={styles.airportName}>
                                {legViewModel.arrivalAirport.name}
                              </div>
                            </div>
                          </div>

                          <div className={styles.flightDurationContainer}>
                            <div
                              data-pw='itineraryCard_legDurationInfo'
                              className={styles.flightDurationLeftColumn}
                            >
                              <div className={styles.duration}>
                                {legViewModel.leg.duration}{' '}
                                <span>{displayViaText(legViewModel.leg.stopoverAirportCodes)}</span>
                              </div>

                              {legViewModel.leg.longStopover ? (
                                <div className={styles.longStopover}>
                                  <HourglassIcon className={styles.icon} />
                                  {translations.long_stopover} - {legViewModel.leg.stopoverDuration}
                                </div>
                              ) : null}

                              {legViewModel.leg.overnight ? (
                                <div className={styles.OvernightFlight}>
                                  <MoonIcon className={styles.icon} />

                                  {translations.overnight_flight}
                                </div>
                              ) : null}
                            </div>

                            <div className={styles.airlineInfo}>
                              <div className={styles.airlineImage}>
                                <img
                                  className={styles.image}
                                  src={cloudinaryImageTransformations(
                                    `https://res.cloudinary.com/wego/image/upload/c_fit,w_200,h_70/${CLOUDINARY_DAILY_KEY}/flights/airlines_rectangular/${legViewModel.airlines[0]?.code}.png`,
                                    'c_fit',
                                    'w_100',
                                    'h_100',
                                    'q_auto',
                                    'f_auto',
                                    'fl_lossy',
                                  )}
                                />

                                {uniq(legViewModel.airlines.map((airline) => airline.code)).length >
                                  1 && (
                                  <div className={styles.additionalNoOfAirlines}>
                                    +
                                    {translateNumber(
                                      uniq(legViewModel.airlines.map((airline) => airline.code))
                                        .length - 1,
                                      locale === 'fa',
                                    )}
                                  </div>
                                )}
                              </div>

                              <span className={styles.operatingAirline}>
                                {getOperatingAirlinesInformation(legViewModel.leg, translations)}
                              </span>
                            </div>
                          </div>

                          <div
                            className={styles.showHideDetails}
                            onClick={() => expandCollapseLegDetails(!isLegExpanded, legIndex)}
                          >
                            <div className={styles.showHideDetailsText}>
                              {isLegExpanded
                                ? translations.hide_details
                                : translations.show_details}

                              <div
                                className={clsx(
                                  styles.chevron,
                                  isLegExpanded ? styles.top : styles.bottom,
                                )}
                              ></div>
                            </div>
                          </div>
                        </div>
                        {/* Empty Itinerary Card */}
                        {tripViewModel.legs.length === 1 ? (
                          <div
                            className={styles.emptyItineraryCard}
                            style={{ width: `${itemWidth}px` }}
                          ></div>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                : null}

              {/* TripViewModel is undefined when the URL with trip id is copy pasted  */}
              {flightFareResult
                ? flightFareResult.trip.legs.map((leg, index) => {
                    const cardTitle =
                      legs.length === 1
                        ? translations.depart
                        : legs.length === 2 && isRoundTrip(legs)
                        ? index === 0
                          ? translations.depart
                          : translations.return
                        : `${translations.flight} ${index + 1}`;
                    const legIndex = index;

                    return (
                      <React.Fragment key={legIndex}>
                        <TripLegSummary
                          cardTitle={cardTitle as string}
                          flightAmenities={flightAmenities}
                          isActiveLeg={legs.length > 2 && activeLeg === legIndex}
                          isLegExpanded={isLegExpanded}
                          itemWidth={itemWidth}
                          leg={leg}
                          legIndex={legIndex}
                          specialFlightMessages={specialFlightMessages}
                          onExpandCollapseLegDetails={expandCollapseLegDetails}
                          setRef={setRef}
                        />

                        {/* Empty Itinerary Card */}
                        {flightFareResult.trip.legs.length === 1 ? (
                          <div
                            className={styles.emptyItineraryCard}
                            style={{ width: `${itemWidth}px` }}
                          ></div>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                : null}
            </LegDetailsCarouselContainer>
          </div>

          {/* Fares */}
          <TripFares
            activeLeg={isPerLegSearch ? legSelectedInFlightSearchResults : 0}
            clientId={clientId}
            flightFareResult={flightFareResult}
            flightMetasearchInfo={flightMetasearchInfo}
            gtmAuth={gtmAuth}
            gtmPreview={gtmPreview}
            isLoadingFares={isLoadingFares}
            passengerCount={passengerCount}
            paymentMethodsFromApi={paymentMethodsFromApi}
            sessionId={clientSessionId}
            isSpecialFlight={specialFlightMessages.length > 0}
            beforeProceed={(handoffUrl) => {
              setIsDialogOpen(true);
              setSpecialFlightHandoffUrl(handoffUrl);
            }}
            impressionLogOnFareClick={impressionLogging.handleFareClicked}
            impressionLogOnFareFilter={impressionLogging.handleFilterApplied}
          />
        </div>
      </div>

      <Dialog isOpen={isDialogOpen} onClose={(e) => handleOnSpecialFlightPopupClose('close', e)}>
        <div className={styles.specialFlightDialog}>
          <div className={styles.dialogContent}>
            {!!specialFlightMessages && specialFlightMessages.length > 0 ? (
              <>
                {/* Display first message only for special flight */}
                {[specialFlightMessages[0]].map((specialFlightMessage, index) => (
                  <Fragment key={`specialFlightDialog${index}`}>
                    <div className={styles.title}>{specialFlightMessage.title}</div>
                    <div
                      className={styles.message}
                      dangerouslySetInnerHTML={{ __html: specialFlightMessage.messages }}
                    />
                  </Fragment>
                ))}
                <div className={styles.buttons}>
                  <div
                    className={clsx(styles.button, styles.invertGreen)}
                    onClick={(e) => handleOnSpecialFlightPopupClose('cancel', e)}
                  >
                    {translations.cancel}
                  </div>
                  <a
                    href={specialFlightHandoffUrl}
                    target='_blank'
                    rel='noreferrer'
                    onClick={(e) => handleOnSpecialFlightPopupClose('confirm', e)}
                    className={styles.button}
                  >
                    {translations.continue}
                  </a>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TripDetail;
